/**
 * OTIF Override dialog — staff/superuser only.
 *
 * Marks a schedule line as OTIF-PASSED with a mandatory reason (captured
 * with who/when and audited to the PO changelog), or removes an existing
 * override. Shared by the Schedule Detail modal and the Current-Week /
 * OTIF drilldown modal so both surfaces use one codepath.
 *
 * Callers gate rendering on `user?.is_superuser || user?.is_staff` — the
 * backend enforces the same rule on the endpoint.
 */
import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, ShieldOff } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { purchasingAPI } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

/** Minimal slice of POLineSchedule the dialog needs — lets GRN-shaped rows reuse it. */
export interface OtifOverrideTarget {
  id: number;
  po_number?: string;
  line_number?: number;
  schedule_line_number?: string;
  part_number?: string;
  otif_overridden?: boolean;
  otif_override_reason?: string;
  otif_overridden_by_name?: string | null;
  otif_overridden_at?: string | null;
}

interface OtifOverrideDialogProps {
  open: boolean;
  schedule: OtifOverrideTarget | null;
  onClose: () => void;
  /** Called after a successful set/clear so the parent can refetch. */
  onDone: () => void;
}

export default function OtifOverrideDialog({ open, schedule, onClose, onDone }: OtifOverrideDialogProps) {
  const { formatDate } = useFormatters();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setReason('');
  }, [open, schedule?.id]);

  if (!schedule) return null;
  const isOverridden = !!schedule.otif_overridden;

  const submit = async (clear: boolean) => {
    if (!clear && !reason.trim()) {
      toast.error('A reason is required to override the OTIF verdict.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await purchasingAPI.overrideScheduleOtif(
        schedule.id,
        clear ? { clear: true } : { reason: reason.trim() },
      );
      toast.success(res.data.detail || (clear ? 'OTIF override removed.' : 'Schedule marked as OTIF-passed.'));
      onDone();
      onClose();
    } catch (err: unknown) {
      console.error('OTIF override failed:', err);
      const axiosError = err as { response?: { data?: { error?: string; detail?: string } } };
      toast.error(
        axiosError.response?.data?.error ||
          axiosError.response?.data?.detail ||
          'Failed to update OTIF override',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const ref = [
    schedule.po_number,
    schedule.line_number != null ? `Line ${schedule.line_number}` : null,
    schedule.schedule_line_number ? `Sched ${schedule.schedule_line_number}` : null,
  ].filter(Boolean).join(' / ');

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isOverridden ? <ShieldOff className="h-5 w-5 text-amber-600" /> : <ShieldCheck className="h-5 w-5 text-emerald-600" />}
            {isOverridden ? 'Remove OTIF Override' : 'Override OTIF Verdict'}
          </DialogTitle>
          <DialogDescription>
            {ref}{schedule.part_number ? <> · <span className="font-mono">{schedule.part_number}</span></> : null}
          </DialogDescription>
        </DialogHeader>

        {isOverridden ? (
          <div className="space-y-2 text-sm">
            <p>This schedule is currently marked as <span className="font-semibold text-emerald-700">OTIF Passed</span> by manual override.</p>
            <div className="rounded-md border bg-slate-50 p-3 space-y-1">
              <p><span className="text-muted-foreground">Reason:</span> {schedule.otif_override_reason || '—'}</p>
              <p className="text-xs text-muted-foreground">
                By {schedule.otif_overridden_by_name || 'Staff user'}
                {schedule.otif_overridden_at ? <> on {formatDate(schedule.otif_overridden_at)}</> : null}
              </p>
            </div>
            <p className="text-muted-foreground">Removing the override restores the calculated OTIF verdict. This is recorded in the audit history.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This marks the schedule as <span className="font-semibold text-emerald-700">OTIF Passed</span> regardless
              of its receipts, across the dashboard and all drilldowns. Who, when and why are recorded in the audit history.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="otif-override-reason">Override reason <span className="text-red-500">*</span></Label>
              <Textarea
                id="otif-override-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Customer-agreed late delivery — supplier not at fault"
                rows={3}
                disabled={submitting}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} type="button">Cancel</Button>
          {isOverridden ? (
            <Button
              variant="destructive"
              onClick={() => submit(true)}
              disabled={submitting}
              type="button"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldOff className="h-4 w-4 mr-2" />}
              Remove Override
            </Button>
          ) : (
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => submit(false)}
              disabled={submitting || !reason.trim()}
              type="button"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Mark OTIF Passed
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
